import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';

export const SNIPPET_CARD_HEIGHT = 100;

const styles = cssMap({
	cardWrapper: {
		position: 'relative',
		display: 'flex',
		width: '100%',
		height: '100px',
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	cardWrapperActive: {
		backgroundColor: token('elevation.surface.hovered'),
	},
	cardButtonOverlay: {
		position: 'absolute',
		top: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		textAlign: 'left',
	},
	cardContentLayer: {
		position: 'relative',
		width: '100%',
		zIndex: 1,
		pointerEvents: 'none',
		paddingInline: token('space.300'),
	},
});

export type SnippetPanelCardProps = {
	children: React.ReactNode;
	onClick: () => void;
	ariaLabel: string;
	testId: string;
	isActive?: boolean;
	isDisabled?: boolean;
	onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
	onMouseLeave?: () => void;
};

/**
 * Shared presentational card for snippet panel list rows (snippet items and create row).
 * Renders a clickable wrapper with overlay and a content layer (preview + custom content).
 */
export const SnippetPanelCard = ({
	children,
	onClick,
	ariaLabel,
	testId,
	isActive = false,
	isDisabled = false,
	onMouseEnter,
	onMouseLeave,
}: SnippetPanelCardProps) => {
	return (
		<Box
			xcss={cx(styles.cardWrapper, isActive && styles.cardWrapperActive)}
			testId={`${testId}-card-wrapper`}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<Pressable
				xcss={styles.cardButtonOverlay}
				onClick={onClick}
				testId={testId}
				aria-label={ariaLabel}
				isDisabled={isDisabled}
			/>
			<Inline
				xcss={styles.cardContentLayer}
				alignInline="start"
				alignBlock="center"
				space="space.200"
			>
				{children}
			</Inline>
		</Box>
	);
};
